// ViewModalTimelineSection.jsx

import React, { useMemo } from 'react';

// Context
import { useTheme } from 'context/ThemeContext';

// Components
import Checkbox from 'components/checkbox/Checkbox';
import CheckboxList from 'components/checkboxlist/CheckboxList';
import SelectInput from 'components/selectinput/SelectInput';

const ROW_MODE_OPTIONS = [
    { value: 'flat', label: 'Flat rows' },
    { value: 'grouped', label: 'Grouped rows' },
];

const PRESET_OPTIONS = [
    { value: 'timeline', label: 'Timeline' },
    { value: 'gantt', label: 'Gantt' },
];

/**
 * ViewModalTimelineSection
 *
 * Renders Timeline-specific view configuration controls.
 *
 * @returns {ReactNode} The rendered component.
 */
const ViewModalTimelineSection = ({
    isVisible,
    timelineField,
    setTimelineField,
    timelineRowMode,
    setTimelineRowMode,
    timelineGroupField,
    setTimelineGroupField,
    showUnscheduled,
    setShowUnscheduled,
    preset = 'timeline',
    setPreset,
    showHierarchy = false,
    setShowHierarchy,
    showDependencies = false,
    setShowDependencies,
    allowScheduleEdit = false,
    setAllowScheduleEdit,
    timelineTemporalFields,
    timelineGroupFields,
}) => {

    // ----- CONTEXT ----- //

    const { theme } = useTheme();

    // ----- MEMOS ----- //

    const temporalOptions = useMemo(() => (
        (Array.isArray(timelineTemporalFields) ? timelineTemporalFields : [])
            .map((field) => {
                const fieldKey = field?.name || field?.key;
                if (!fieldKey) return null;
                const fieldType = String(field?.type || '').toLowerCase();
                const baseLabel = String(field?.label || '').trim();
                const typeSuffix = fieldType === 'datetimerange' || fieldType === 'datetime'
                    ? ' (date + time)'
                    : '';
                return {
                    value: fieldKey,
                    label: baseLabel ? `${baseLabel}${typeSuffix}` : '',
                };
            })
            .filter(Boolean)
    ), [timelineTemporalFields]);

    const groupOptions = useMemo(() => (
        (Array.isArray(timelineGroupFields) ? timelineGroupFields : [])
            .map((field) => {
                const fieldKey = field?.name || field?.key;
                if (!fieldKey) return null;
                return {
                    value: fieldKey,
                    label: String(field?.label || '').trim(),
                };
            })
            .filter(Boolean)
    ), [timelineGroupFields]);

    const normalizedPreset = String(preset || 'timeline').trim().toLowerCase() === 'gantt'
        ? 'gantt'
        : 'timeline';

    if (!isVisible) return null;

    // ----- RENDER ----- //

    return (
        <div className="view-modal-section">
            <div className="view-modal-section-header">
                <div
                  className="view-modal-section-title"
                  style={{
                    color: theme.foregroundMuted,
                  }}
                >{normalizedPreset === 'gantt' ? 'Gantt' : 'Timeline'}</div>
            </div>

            <div
              className="view-modal-row"
              style={{
                gridTemplateColumns: '1fr',
              }}
            >
                <SelectInput
                  style={{
                    fontSize: '12px',
                  }}
                  value={normalizedPreset}
                  options={PRESET_OPTIONS}
                  placeholder="Select preset..."
                  onChange={(e) => {
                    const nextPreset = e.target.value === 'gantt' ? 'gantt' : 'timeline';
                    if (typeof setPreset === 'function') setPreset(nextPreset);
                    if (nextPreset === 'gantt') {
                      if (typeof setShowHierarchy === 'function') setShowHierarchy(true);
                      if (typeof setShowDependencies === 'function') setShowDependencies(true);
                      if (typeof setAllowScheduleEdit === 'function') setAllowScheduleEdit(true);
                      return;
                    }
                    if (typeof setShowHierarchy === 'function') setShowHierarchy(false);
                    if (typeof setShowDependencies === 'function') setShowDependencies(false);
                    if (typeof setAllowScheduleEdit === 'function') setAllowScheduleEdit(false);
                  }}
                />
            </div>

            <div
              className="view-modal-row"
              style={{
                gridTemplateColumns: '1fr',
              }}
            >
                <SelectInput
                  style={{
                    fontSize: '12px',
                  }}
                  value={timelineField || ''}
                  options={temporalOptions}
                  placeholder="Select timeline field..."
                  onChange={(e) => setTimelineField(e.target.value)}
                />
            </div>

            {temporalOptions.length === 0 && (
                <div
                  className="view-modal-empty"
                  style={{
                    color: theme.foregroundMuted,
                  }}
                >
                    Add a date range or date-time range field in this node type to use Timeline.
                </div>
            )}

            <div className="view-modal-section-header">
                <div
                  className="view-modal-section-title"
                  style={{
                    color: theme.foregroundMuted,
                  }}
                >Row layout</div>
            </div>
            <CheckboxList
              value={(timelineRowMode === 'grouped') ? 'grouped' : 'flat'}
              options={ROW_MODE_OPTIONS}
              onChange={(nextValue) => setTimelineRowMode(nextValue)}
            />

            {timelineRowMode === 'grouped' && (
                <div
                  className="view-modal-row"
                  style={{
                    gridTemplateColumns: '1fr',
                  }}
                >
                    <SelectInput
                      style={{
                        fontSize: '12px',
                      }}
                      value={timelineGroupField || ''}
                      options={groupOptions}
                      placeholder="Group by field..."
                      onChange={(e) => setTimelineGroupField(e.target.value)}
                    />
                </div>
            )}

            <Checkbox
              label="Show unscheduled items"
              value={Boolean(showUnscheduled)}
              size={24}
              onChange={() => setShowUnscheduled((prev) => !prev)}
            />

            <Checkbox
              label="Show hierarchy"
              value={Boolean(showHierarchy)}
              size={24}
              onChange={() => setShowHierarchy((prev) => !prev)}
            />

            <Checkbox
              label="Show predecessor links"
              value={Boolean(showDependencies)}
              size={24}
              onChange={() => setShowDependencies((prev) => !prev)}
            />

            <Checkbox
              label="Allow schedule drag"
              value={Boolean(allowScheduleEdit)}
              size={24}
              onChange={() => setAllowScheduleEdit((prev) => !prev)}
            />
        </div>
    );
};

// Dev Tools
ViewModalTimelineSection.displayName = 'ViewModalTimelineSection';

export default ViewModalTimelineSection;
